import { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { CalendarDays, ClipboardList, LayoutDashboard, LogOut, ShieldCheck } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import 'bootstrap-icons/font/bootstrap-icons.css'

const logoMark = process.env.PUBLIC_URL + '/images/logo_shape.png'
const accreditationLogo = process.env.PUBLIC_URL + '/images/acredited.jpeg'

// Sidebar look follows BirBook's icon rail: gradient-stroked icons with a
// permanent label underneath, a left accent bar for the active entry.
const ICON_GRADIENT_ID = 'jnj-icon-gradient'
const PRIMARY = '#0a5fe0'

// userSpaceOnUse (each icon's own 24x24 space) so straight strokes still get painted
const IconGradientDefs = () => (
  <svg aria-hidden="true" focusable="false" width="0" height="0" className="absolute">
    <defs>
      <linearGradient id={ICON_GRADIENT_ID} gradientUnits="userSpaceOnUse" x1="3" y1="22" x2="21" y2="2">
        <stop offset="0%" stopColor="#0029a4" />
        <stop offset="48%" stopColor="#016df7" />
        <stop offset="100%" stopColor="#0dbafd" />
      </linearGradient>
    </defs>
  </svg>
)

const NavItem = ({ icon: Icon, label, isActive, onClick }) => (
  <button type="button" onClick={onClick} className="block w-full" aria-current={isActive ? 'page' : undefined}>
    <span className="relative flex w-full flex-col items-center gap-0.5 rounded-xl px-1 py-1.5 transition-colors hover:bg-[#dbe7ff]/60">
      {isActive && (
        <span
          aria-hidden
          className="absolute top-1/2 -left-1 h-8 w-1 -translate-y-1/2 rounded-r-full"
          style={{ backgroundColor: PRIMARY }}
        />
      )}
      <span className="relative flex h-10 w-11 shrink-0 items-center justify-center">
        <Icon
          className={`h-10 w-10 transition-transform duration-200 ${isActive ? 'scale-105' : ''}`}
          strokeWidth={1.9}
          style={{ stroke: `url(#${ICON_GRADIENT_ID})` }}
        />
      </span>
      <span
        className={`max-w-full truncate text-[11px] leading-tight ${isActive ? 'font-semibold' : 'font-medium'}`}
        style={{ color: PRIMARY }}
      >
        {label}
      </span>
    </span>
  </button>
)

const initialsFor = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?'

const Layout = () => {
  const { logout, user, userGroups, hasAdminAccess, isFinanceManager } = useAuth()
  const isReadOnlyClient = userGroups.includes('ReadOnlyClient')
  const isManager = userGroups.includes('Manager')
  const isStaff = userGroups.includes('Staff')
  const navigate = useNavigate()
  const location = useLocation()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef(null)

  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false)
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [showUserMenu])

  const isActive = (path) => {
    // Exact match
    if (location.pathname === path) return true

    // Special handling for /surveys to exclude /surveys/new
    if (path === '/surveys') {
      // Active for /surveys or /surveys/:id/edit, /surveys/:id/invoice, etc.
      // But NOT for /surveys/new
      return location.pathname.startsWith('/surveys/') &&
             !location.pathname.startsWith('/surveys/new')
    }

    // For other paths, check if it starts with path + '/'
    return location.pathname.startsWith(path + '/')
  }

  // Clicking the page you're already on reloads it to refresh its data
  const goTo = (path) => {
    if (isActive(path)) {
      window.location.reload()
    } else {
      navigate(path)
    }
  }

  const goHome = () => {
    if (location.pathname === '/') {
      // Refresh home data without a full page reload (avoids auth issues)
      window.dispatchEvent(new CustomEvent('refreshHomePage'))
    } else {
      navigate('/')
    }
  }

  const navItems = [
    {
      path: '/surveys',
      label: 'Surveys',
      icon: ClipboardList,
      visible: isManager || isFinanceManager || hasAdminAccess,
    },
    { path: '/operations', label: 'Operations', icon: CalendarDays, visible: true },
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      visible: isStaff || isManager || isFinanceManager || hasAdminAccess || isReadOnlyClient,
    },
  ].filter((item) => item.visible)

  const displayName = user
    ? user.welcome_name ||
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.username
    : ''

  return (
    <div className="min-h-screen bg-[#f4f7fc]">
      <aside className="print-hidden fixed inset-y-0 left-0 z-50 flex w-20 flex-col border-r border-[#d9e2f1] bg-white text-[#0f1b33]">
        <IconGradientDefs />

        <div className="flex h-16 shrink-0 items-center justify-center px-1">
          <button
            type="button"
            onClick={goHome}
            aria-label="Home"
            className="flex h-[52px] w-[52px] items-center justify-center rounded-xl transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a5fe0]"
          >
            <img src={logoMark} alt="J and J Marine Surveys" className="h-11 w-11" draggable="false" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-1.5 overflow-y-auto [scrollbar-width:none] border-t border-[#d9e2f1] px-1 py-2">
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              isActive={isActive(item.path)}
              onClick={() => goTo(item.path)}
            />
          ))}
        </nav>

        {hasAdminAccess && (
          <div className="flex flex-col gap-0.5 border-t border-[#d9e2f1] px-1 py-2">
            <NavItem icon={ShieldCheck} label="Admin" isActive={isActive('/admin')} onClick={() => goTo('/admin')} />
          </div>
        )}

        {user && (
          <div ref={userMenuRef} className="relative flex flex-col items-center gap-1 border-t border-[#d9e2f1] p-2">
            <button
              type="button"
              onClick={() => setShowUserMenu((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
              title={displayName}
              className="flex w-full items-center justify-center rounded-lg py-1.5 transition-colors hover:bg-[#dbe7ff]"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e8eef8] text-xs font-medium text-[#0f1b33]">
                {initialsFor(displayName)}
              </span>
            </button>

            {showUserMenu && (
              <div
                role="menu"
                className="absolute bottom-2 left-full z-50 ml-3 w-56 rounded-lg border border-[#d9e2f1] bg-white p-1 text-sm shadow-lg"
              >
                <div className="flex flex-col gap-0.5 px-2 py-1.5">
                  <span className="truncate font-medium">{displayName || 'Account'}</span>
                  {user.email && <span className="truncate text-xs text-[#55657f]">{user.email}</span>}
                </div>
                <div className="-mx-1 my-1 h-px bg-[#d9e2f1]" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={logout}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      <div className="pl-20 print:pl-0">
        {/* Top bar, level with the sidebar's logo cell */}
        <header className="print-hidden sticky top-0 z-40 h-16 overflow-hidden border-b border-[#d9e2f1] bg-white/95 backdrop-blur">
          <div className="layout-accreditation absolute top-1/2 rounded-lg border border-[#d9e2f1] bg-white p-1">
            <img src={accreditationLogo} alt="SAC Accredited Inspection Body" className="h-10 w-auto" draggable="false" />
          </div>
        </header>
        <main>
          <Outlet />
        </main>
      </div>

      <style>{`
        @keyframes layoutBadgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(1, 109, 247, 0.35); }
          50% { box-shadow: 0 0 0 5px rgba(1, 109, 247, 0.08), 0 0 16px rgba(1, 109, 247, 0.25); }
        }
        /* Travels left to right across the top bar, then glides back */
        @keyframes layoutBadgeTravel {
          from { left: 1rem; }
          to { left: calc(100% - 5rem); }
        }
        .layout-accreditation {
          left: 1rem;
          transform: translateY(-50%);
          animation:
            layoutBadgeTravel 14s ease-in-out infinite alternate,
            layoutBadgePulse 3s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .layout-accreditation { animation: none; }
        }
      `}</style>
    </div>
  )
}

export default Layout
